import { useMutation } from "@apollo/client";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import {
  Checkbox,
  Label,
  Page,
  Text,
  VStack,
  View,
  useFlashMessages,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import {
  Product,
  ProductImage,
} from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, useWindowDimensions } from "react-native";
import { Spacings } from "react-native-ui-lib";
import { SortableGrid } from "../../../../../../components";
import {
  DragItem,
  ItemTypes,
  SortItem,
} from "../../../../../../components/DragDrop/types";
import { keys } from "../../../../../../utilities/i18n";
import { saveRecentProduct } from "../../../../../../utilities/recent-products";
import { useItemSizing } from "../../../../../../utilities/photo-image/utils";
import { toTitleCase } from "../../../../../../utilities/to-title-case";
import {
  AddImageTile,
  ImageTile,
  MediaCard,
  ProductPreview,
} from "../../../../components";
import { useImagesSentSuccessProvider } from "../../../../components/ImagesSentSuccessProvider/hooks";
import { useSelectUploadImageProvider } from "../../../../components/SelectUploadImageProvider/hooks";
import { ProductImageDetailsScreenFooter } from "../ProductImageDetailsScreenFooter";
import { UPDATE_PRODUCT_IMAGE_POSITION } from "./constants";
import React from "react";
import { MaterialIcons } from "@expo/vector-icons";

const ADD_IMAGE_ID = "add-image";

type Item<T extends ProductImage = any> = DragItem<T> | SortItem;

interface ProductImageDetailsScreenInnerProps {
  product: Product;
  images: ProductImage[];
}

export function ProductImageDetailsScreenInner({
  product,
  images,
}: ProductImageDetailsScreenInnerProps) {
  const { showMessage } = useFlashMessages();
  const [gridKey, setGridKey] = useState<number>(1);

  const windowDimensions = useWindowDimensions();

  const [updateProductImagePosition] = useMutation<{
    updateProductImagePosition: ProductImage[];
  }>(UPDATE_PRODUCT_IMAGE_POSITION);

  const { updateHandlers: updateSelectUploadImageHandlers } =
    useSelectUploadImageProvider();
  const { updateHandlers: updateImagesSentSuccessHandlers } =
    useImagesSentSuccessProvider();

  const { t } = useTranslation();
  const { colors, spacings } = useThemeProvider();
  const [order, setOrder] = useState(generateOrder());

  const [selected, setSelected] = useState<string[]>([]);
  const { navigate, goBack, pop } = useNavigation<any>();
  const { imageSize, numColumns } = useItemSizing();

  const [isDragging, setIsDragging] = useState<boolean>(false);

  function generateOrder() {
    return [ADD_IMAGE_ID, ...images.map((image) => image.id)];
  }

  const dataMap = useMemo(() => {
    const map = new Map<string, Item>();

    map.set(ADD_IMAGE_ID, {
      id: ADD_IMAGE_ID,
      type: ItemTypes.Sortable,
    });

    for (const image of images) {
      map.set(image.id, {
        id: image.id,
        type: ItemTypes.Draggable,
        data: image,
      });
    }

    return map;
  }, [images]);

  function handleSelect(id: string) {
    setSelected((selected) => {
      if (selected.includes(id)) {
        return selected.filter((s) => s !== id);
      } else {
        return [...selected, id];
      }
    });
  }

  function handleSelectAll(checked: boolean) {
    if (checked && selected.length < images.length) {
      setSelected(images.map((image) => image.id));
    } else if (!checked && selected.length === images.length) {
      setSelected([]);
    }
  }

  function renderItem<T extends ProductImage>(item: Item<T>, index: number) {
    switch (item.type) {
      case ItemTypes.Draggable: {
        const isSelected = selected.includes(item.data.id);

        return (
          <ImageTile
            selected={isSelected}
            onSelect={() => handleSelect(item.data.id)}
            width={imageSize}
            height={imageSize}
            index={index}
            imageUrl={item.data.path}
          />
        );
      }
      case ItemTypes.Sortable:
        return renderSortableItem(item, index);
    }
  }

  function renderSortableItem<T>(item: SortItem<T>, index: number) {
    const commonProps = {
      width: imageSize,
      height: imageSize,
      margin: margin,
    };
    switch (item.id) {
      case ADD_IMAGE_ID: {
        return (
          <AddImageTile
            {...commonProps}
            onPress={() => {
              navigate("SelectUploadImageMethodScreen", {
                sku: product.stockNumber, // Include sku in the SelectUploadImageMethodScreen params
              });
            }}
          />
        );
      }
      default:
        return <></>;
    }
  }

  function handleOrderingFinished(newOrder: string[]) {
    setOrder(newOrder);
  }

  async function handleDrop(id: string, position: number) {
    return updateProductImagePosition({
      variables: {
        input: {
          id,
          position,
        },
      },
    }).catch(() => {
      showMessage({
        severity: "error",
        message: "Error updating image position",
      });
    });
  }

  useEffect(() => {
    setOrder(generateOrder());
  }, [images]);

  useEffect(() => {
    setGridKey((prev) => (prev += 1));
  }, [windowDimensions]);

  useFocusEffect(
    useCallback(() => {
      updateSelectUploadImageHandlers({
        onUpload: () => {
          saveRecentProduct({
            id: product.id,
            name: product.name,
            stockNumber: product.stockNumber,
          });
        },
      });

      updateImagesSentSuccessHandlers({
        onTakeMore: (nav) => nav.goBack(),
        onNewProduct: (nav) => nav.pop(2),
        onFinished: (nav) => nav.pop(3),
      });
    }, [updateSelectUploadImageHandlers, updateImagesSentSuccessHandlers, product])
  );

  const hasSelections = !!selected.length;

  let headerText = t(keys.media);

  if (isDragging) {
    headerText = t(keys.imageDragged);
  } else if (hasSelections) {
    headerText = t(keys.imagesSelected, { count: selected.length });
  }

  const margin = spacings.s3;

  return (
    <>
      <Page
        cover
        title={`${toTitleCase(product.name)} (${product.displayName})`}
        secondaryTitle={t(keys.manageProductImages)}
        scrollable={false}
        footer={
          <ProductImageDetailsScreenFooter
            productId={product.id}
            selected={selected}
          />
        }
        footerStyles={{
          paddingLeft: Spacings.s5,
          paddingRight: Spacings.s5,
          paddingTop: spacings.s3,
          paddingBottom: spacings.s3,
          marginTop: 0,
        }}
      >
        <VStack fill>
          <ProductPreview product={product} loading={false} />
          <VStack fill spacing={spacings.s2}>
            <MediaCard
              titleMarkup={
                <>
                  <Text
                    variant="heading2"
                    fontWeight="medium"
                    numberOfLines={1}
                  >
                    {headerText}
                  </Text>
                  <Label
                    label="Select all"
                    style={{ paddingRight: spacings.s1 }}
                  >
                    <Checkbox
                      color={colors.primary}
                      value={selected.length === images.length}
                      onValueChange={handleSelectAll}
                    />
                  </Label>
                </>
              }
            >
              <SortableGrid
                key={gridKey}
                data={dataMap}
                initialOrder={order}
                renderItem={renderItem}
                onOrderingFinished={handleOrderingFinished}
                onEnd={() => setIsDragging(false)}
                onDrop={handleDrop}
                itemWidth={imageSize}
                itemHeight={imageSize}
                numColumns={numColumns}
                itemMargin={margin}
                offsetY={margin}
                DragHandleContainer={DragHandleContainer}
                DragHandleComponent={DragHandle}
                onStart={() => {
                  setIsDragging(true);
                }}
              />
            </MediaCard>
          </VStack>
        </VStack>
      </Page>
    </>
  );
}

function DragHandleContainer({ children }: React.PropsWithChildren<{}>) {
  const { spacings } = useThemeProvider();

  return (
    <View
      style={{
        position: "absolute",
        right: spacings.s2,
        top: spacings.s2,
      }}
    >
      {children}
    </View>
  );
}

const DragHandle = React.forwardRef<any, {}>(({ ...props }, ref) => {
  const { spacings, colors } = useThemeProvider();

  const commonPressableProps = {
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    borderRadius: spacings.s2,
  };

  const commonIconProps = {
    size: 24,
    color: colors.black,
  };

  return (
    <Pressable ref={ref} {...props} style={commonPressableProps}>
      <MaterialIcons {...commonIconProps} name="drag-indicator" />
    </Pressable>
  );
});
